import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { Box, Grid, TextField, Autocomplete, InputAdornment, IconButton, Tooltip, TextFieldProps, Button, } from '@mui/material';
import { Search, Refresh, CampaignOutlined, PeopleAlt } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchTerm, setSelectedCamp, setDateRange, clearFilters, setStatus } from '../../redux/reducer/CommonFilterSlice';
import { CampListingService } from '../../utils/services/camp.service';
import { formatCampLabel } from '../../utils/campLabel';
import DateRangePicker from './DateRangePicker';
import { Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';

interface HealthCamp {
    camp_name: string;
    camp_code?: string;
    district?: string;
}

interface SearchFilterBarProps {
    totalItems?: number;
    hide?: boolean;
    searchLabel?: string;
}

const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
    totalItems = 0,
    hide = false,
    searchLabel
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const isDashboardView = searchParams.get('nav') === 'dashboard';

    const { selectedCamp, startDate, endDate, status } = useSelector((state: any) => state.filters);
    const [healthCamps, setHealthCamps] = React.useState<HealthCamp[]>([]);
    const [inputValue, setInputValue] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    // ✅ Status options with translation - useMemo to update when language changes
    const statusOptions = useMemo(() => [
        { label: t('searchFilterBar', 'ongoing'), value: 'ongoing' },
        { label: t('searchFilterBar', 'completed'), value: 'completed' },
    ], [t]);

    // ✅ Use translated search placeholder if not provided
    const translatedSearchLabel = searchLabel || t('searchFilterBar', 'searchPlaceholder');

    // ✅ FIXED: Removed 't' from dependencies to avoid infinite loop
    const fetchHealthCamps = useCallback(async () => {
        if (isDashboardView || hide) {
            setHealthCamps([]);
            dispatch(setSelectedCamp(null));
            return;
        }

        setLoading(true);
        const payload = { status: 'approved', slug: status };
        const response = await CampListingService(payload);
        const { code, data } = response;
        if (code === 200 && data?.data) {
            setHealthCamps(data?.data);
            if (!selectedCamp) {
                dispatch(setSelectedCamp(data.data[0] ?? null));
            }
        } else {
            setHealthCamps([]);
            dispatch(setSelectedCamp(null));
        }
        setLoading(false);
    }, [dispatch, status, isDashboardView, selectedCamp, hide]); // ✅ NO 't' here

    useEffect(() => {
        fetchHealthCamps();
    }, [fetchHealthCamps]);

    const handleDateRangeChange = (start: Dayjs | null, end: Dayjs | null) => {
        dispatch(setDateRange({ startDate: start, endDate: end }));
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            dispatch(setSearchTerm(inputValue));
        }, 300);

        return () => clearTimeout(timeout);
    }, [inputValue, dispatch]);

    const handleResetFilters = () => {
        dispatch(clearFilters());
        setInputValue('');
        if (!isDashboardView) {
            fetchHealthCamps();
        }
    };

    return (
        <Box sx={{ borderBottomStyle: 'solid', borderBottomColor: 'grey.300', backgroundColor: 'common.white' }}>
            <Grid container spacing={1} sx={{ px: 1, pt: 1, pb: 0.4 }}>
                {!hide && !isDashboardView && (
                    <>
                        <Grid item xs={12} sm={6} md={1}>
                            <Autocomplete
                                options={statusOptions}
                                size="small"
                                disableClearable
                                value={statusOptions.find(option => option.value === status) ?? { label: '', value: '' }}
                                getOptionLabel={(option) => option?.label ?? ''}
                                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                renderInput={(params) => (
                                    <TextField
                                        {...params as any}
                                        label={t('searchFilterBar', 'status')}
                                    />
                                )}
                                onChange={(event, newValue) => {
                                    dispatch(setStatus(newValue?.value ?? null));
                                    dispatch(setSelectedCamp(null));
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box>
                                <Autocomplete
                                    size="small"
                                    disablePortal
                                    options={healthCamps}
                                    value={selectedCamp}
                                    disableClearable
                                    loading={loading}
                                    getOptionLabel={(option) => formatCampLabel(option)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...(params as TextFieldProps)}
                                            label={t('searchFilterBar', 'selectHealthCamp')}
                                            variant="outlined"
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CampaignOutlined color="primary" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}
                                    onChange={(event, newValue) => dispatch(setSelectedCamp(newValue ?? null))}
                                />
                            </Box>
                        </Grid>
                    </>
                )}

                <Grid item xs={12} sm={6} md={hide || isDashboardView ? 4 : 2}>
                    <TextField
                        placeholder={translatedSearchLabel}
                        fullWidth
                        size="small"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title={t('searchFilterBar', 'clickToSearch')}>
                                        <IconButton disableRipple edge="end" sx={{ mr: 0 }}>
                                            <Search />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={hide || isDashboardView ? 4 : 3}>
                    <DateRangePicker
                        onChange={handleDateRangeChange}
                        startDate={startDate}
                        endDate={endDate}
                        resetTrigger={false}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Tooltip title={t('searchFilterBar', 'clickToResetFilters')} arrow>
                            <IconButton disableRipple onClick={handleResetFilters} edge="end">
                                <Refresh />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Grid>
                {(hide || isDashboardView) && (
                    <Grid item xs={12} sm={12} md={1}></Grid>
                )}
                <Grid item xs={12} sm={12} md={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                        <Button variant="outlined" size="small" startIcon={<PeopleAlt />}>
                            {t('searchFilterBar', 'total')} : {totalItems}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SearchFilterBar;
